import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useColorTheme } from '../context/ColorThemeContext';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CreditPurchaseModal = ({ isOpen, onClose, onSuccess, sessionToken }) => {
  const { currentColors, colorTheme } = useColorTheme();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      // Fetch active packages (public endpoint, no auth needed for viewing)
      const response = await axios.get(`${API}/api/admin/credits/credit-packages`);
      const activePackages = response.data.packages
        .filter(pkg => pkg.is_active)
        .sort((a, b) => a.display_order - b.display_order);
      setPackages(activePackages);
    } catch (err) {
      setError('Failed to load credit packages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    try {
      setLoading(true);
      setError(null);

      console.log('CreditPurchaseModal sessionToken:', sessionToken);

      if (!sessionToken) {
        setError('Please login to purchase credits');
        setLoading(false);
        return;
      }

      // Get origin URL from window
      const originUrl = window.location.origin;

      // Create checkout session
      const response = await axios.post(
        `${API}/api/payments/create-checkout-session`,
        {
          package_id: packageId,
          origin_url: originUrl
        },
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getThemeClasses = () => {
    if (colorTheme === 'purple') {
      return {
        gradient: 'from-purple-500 to-pink-500',
        button: 'bg-purple-600 hover:bg-purple-700',
        border: 'border-purple-200 dark:border-purple-700',
        badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      };
    } else if (colorTheme === 'blue') {
      return {
        gradient: 'from-blue-500 to-sky-500',
        button: 'bg-blue-600 hover:bg-blue-700',
        border: 'border-blue-200 dark:border-blue-700',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      };
    }
    return {
      gradient: 'from-green-500 to-emerald-500',
      button: 'bg-green-600 hover:bg-green-700',
      border: 'border-green-200 dark:border-green-700',
      badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    };
  };

  const theme = getThemeClasses();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-4`}>
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white flex items-center">
                <span className="mr-2">💳</span>
                Purchase Credits
              </h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {error && (
              <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-200 rounded-lg">
                {error}
              </div>
            )}

            {loading && packages.length === 0 ? (
              <div className="flex justify-center items-center py-12">
                <div className={`animate-spin rounded-full h-12 w-12 border-b-2 border-${colorTheme}-600`}></div>
              </div>
            ) : packages.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                No credit packages available at the moment
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`relative bg-white dark:bg-gray-700 rounded-lg border-2 ${theme.border} p-6 hover:shadow-xl transition-all`}
                  >
                    {/* Badge */}
                    {pkg.badge_text && (
                      <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${theme.badge}`}>
                        {pkg.badge_text}
                      </div>
                    )}

                    {/* Package Info */}
                    <div className="text-center mb-6">
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {pkg.name}
                      </h4>
                      <div className="mb-4">
                        <span className="text-4xl font-extrabold text-gray-900 dark:text-white">
                          ${pkg.price_usd}
                        </span>
                      </div>
                      <div className="text-2xl font-bold mb-2" style={{ color: currentColors.textColor.split(' ')[0].replace('text-', '#') }}>
                        {pkg.credits} Credits
                      </div>
                      {pkg.discount_percentage > 0 && (
                        <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                          Save {pkg.discount_percentage}%
                        </div>
                      )}
                    </div>

                    {/* What you can do */}
                    <div className="mb-6 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex items-center">
                        <span className="mr-2">📝</span>
                        <span>~{Math.floor(pkg.credits / 3)} text ads</span>
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">🖼️</span>
                        <span>~{Math.floor(pkg.credits / 15)} image ads</span>
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">🎬</span>
                        <span>~{Math.floor(pkg.credits / 50)} video ads</span>
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">🎯</span>
                        <span>~{Math.floor(pkg.credits / 65)} campaigns</span>
                      </div>
                    </div>

                    {/* Purchase Button */}
                    <button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={loading}
                      className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors ${theme.button} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {loading ? 'Processing...' : 'Purchase Now'}
                    </button>

                    {/* Cost per credit */}
                    <div className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
                      ${(pkg.price_usd / pkg.credits).toFixed(3)} per credit
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Info footer */}
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-start">
                <span className="text-2xl mr-3">🔒</span>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  <p className="font-semibold mb-1">Secure Payment via Stripe</p>
                  <p>Your payment information is processed securely. We don't store your card details.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchaseModal;
